import PostBody from '../../components/posts/post-body'
import {getPostBySlug, getAllPosts} from '../../lib/api'
import Head from 'next/head'
import markdownToHtml from '../../lib/markdownToHtml'
import type PostType from '../../interfaces/post'
import FooterView from "../../components/common/Footer";
import {handleLogout} from "components/utils"; import React from "react";
import DateFormatter from "../../components/posts/date-formatter";
import {useRouter} from "next/router";
import moreStories from "../../components/posts/more-stories";
import Link from "next/link";

type Props = {
    post: any
    morePosts: any[]
}

export default function Post({post, morePosts}: Props) {
    const router = useRouter()
    if (!router.isFallback && !post?.slug) {
        return <>404</>
    }
    return (
        <>
            <Head>
                <title>{post && post.title + "| QMe - Hospital & Clinic Management"}</title>
                <meta name="description" content={post.excerpt}/>
                <link rel="canonical" href="https://www.qme.co.in/" />
                <meta property="og:image" content="/og.png"/>
                <meta property="og:title" content="Effortless Patient Management & Token-Based Appointments | QME"/>
                <meta property="og:description" content="Experience efficient stock control, innovative token-based scheduling, and smart queues for unparalleled patient experiences at QME. Read for more!"/>
                <meta name="twitter:image" content="/og.png"/>
                <meta name="robots" content="index, follow"/><script
                    key="schema-jsonld"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{ __html: JSON.stringify({
                            "@context": "https://schema.org",
                            "@type": "Organization",
                            "name": "QMe - Hospital and Clinic Management",
                            "image": "https://www.qme.co.in/logo.svg",
                            "description": "Effortless patient management, efficient stock and inventory control, unparalleled patient experience through innovative token-based appointments and smart queues and much more.",
                            "url": "https://www.qme.co.in/",
                            "telephone": "+919971513514",
                            "emailid": "contact@qme.co.in",
                            "sameAs": ["https://www.facebook.com/qmehmis", "https://www.instagram.com/qmehmis", "https://www.youtube.com/@qmehmis"]
                        }, null, "\t") }}
                />
            </Head>
            <div className="bg-white blogs">
                <div className="row">
                    {router.isFallback ? (
                        <h1>Loading…</h1>
                    ) : (
                        <div className="col col-12 col-lg-10 offset-lg-1 card">
                            <div className="row">
                                <div className="col col-12 col-lg-3 my-5 d-none d-lg-block bg-lightGrey m-lg-5 overflow-scroll" style={{maxHeight: "2000px"}}>
                                    <div className="ml-3 mt-3 fw-bold fs-5">Similar Topics</div>
                                    <ul>
                                        {morePosts.map(ms => {
                                            return <li key={ms.slug} className="text-primary mr-3 text-decoration-none my-3"><Link
                                                as={`/posts/${ms.slug}`}
                                                href="/posts/[ms.slug]"
                                                target="_blank"
                                                className=""
                                            >
                                                {ms.title}
                                            </Link>
                                            </li>
                                        })}
                                    </ul>
                                </div>
                                <div className="col col-12 col-lg-8 my-5">
                                    <h1 className="text-primary fw-bold">{post && post.title}</h1>
                                    <div className="mb-5 text-secondary">
                                        <DateFormatter dateString={post?.date}/>
                                    </div>
                                    <PostBody content={post && post.contentHtml}/>
                                </div>
                                <div className="col col-12 col-lg-3 my-5 d-lg-none bg-lightGrey m-lg-5">
                                    <div className="ml-3 mt-3 fw-bold fs-5">Similar Topics</div>
                                    <ul>
                                        {morePosts.map(ms => {
                                            return <li key={ms.slug} className="text-primary mr-3 text-decoration-none my-3 content"><Link
                                                as={`/posts/${ms.slug}`}
                                                href="/posts/[ms.slug]"
                                                target="_blank"
                                                className=""
                                            >
                                                {ms.title}
                                            </Link>
                                            </li>
                                        })}
                                    </ul>
                                </div>
                            </div>
                        </div>)
                    }
                </div>
                <FooterView/>
            </div>
        </>
    )
}

type Params = {
    params: {
        slug: string
    }
}

export async function getStaticProps({params}: Params) {
    const post = getPostBySlug(params.slug, [
        'title',
        'date',
        'slug',
        'excerpt',
        'content',
    ])
    const contentHtml = await markdownToHtml(post.content || '')
    const allPosts = getAllPosts([
        'title',
        'date',
        'slug',
        'author',
        'coverImage',
        'excerpt',
    ]).filter(p => p.title != post.title )

    return {
        props: {
            post: {
                ...post,
                contentHtml,
            },
            morePosts: allPosts
        }
    }
}

export async function getStaticPaths() {
    const posts = getAllPosts(['slug'])

    return {
        paths: posts.map((post) => {
            return {
                params: {
                    slug: post.slug,
                },
            }
        }),
        fallback: false,
    }
}
